import type { MetadataFields } from './MetadataFields.js'
import type { BoundOperation, ProjectionResult, SampleBucket } from './OperationTypes.js'
import { defaultKey, defaultLabel } from './OperationTypes.js'
import type { FilterOperationKey, GroupByOperationKey, ProjectionOperationKey, SortByOperationKey } from './OperationRegistry.js'
import { OPERATION_REGISTRY } from './OperationRegistry.js'
import type { VisualizationSampleContainer } from './VisualizationSampleContainer.js'

export interface VisualizationPipeline {
  filters: BoundOperation<FilterOperationKey, unknown>[]
  /* Note: currently only exposed in conjunction with groupBy's*/
  projections: BoundOperation<ProjectionOperationKey, unknown>[]
  groupBys: BoundOperation<GroupByOperationKey, unknown>[]
  sortBys: BoundOperation<SortByOperationKey, unknown>[]
}

/* Re-exported from their new home so existing importers of RunPipeline keep working. */
export { defaultKey, defaultLabel }

export function runPipeline(
  samples: VisualizationSampleContainer[],
  pipeline: VisualizationPipeline,
  metadataFields: MetadataFields
): SampleBucket[] {
  let workingSamples = samples

  // 1. Apply all filters
  for (const bound of pipeline.filters) {
    const op = OPERATION_REGISTRY[bound.operationKey]
    if (op?.type !== 'filter') continue

    const field = metadataFields[bound.metaDataField!]
    if (!field) {
      console.warn('Skipping filter: unknown field', bound.metaDataField)
      continue
    }

    const config = bound.config ? bound.config : op.config.defaultConfig
    // @ts-expect-error its hard to get this correct typing wise and we have ensured this must work
    workingSamples = op.apply(workingSamples, field, config)
  }

  // 2. Apply projections (populate per-sample "projection map")
  const projections: Record<string, (sample: VisualizationSampleContainer) => ProjectionResult> = {}

  for (const bound of pipeline.projections) {
    const op = OPERATION_REGISTRY[bound.operationKey]
    if (op?.type !== 'projection') continue

    const field = metadataFields[bound.metaDataField!]
    if (!field) {
      console.warn('Skipping projection: unknown field', bound.metaDataField)
      continue
    }
    const config = bound.config ? bound.config : op.config.defaultConfig
    projections[bound.metaDataField!] = (sample) => op.apply(sample, field, config)
  }

  // 3. Start with a single bucket
  let buckets: SampleBucket[] = [{ label: defaultLabel, key: defaultKey, samples: workingSamples }]

  // 4. Apply groupBys
  for (const bound of pipeline.groupBys) {
    const op = OPERATION_REGISTRY[bound.operationKey]
    if (op?.type !== 'groupBy') continue

    const field = metadataFields[bound.metaDataField!]
    if (!field) {
      console.warn('Skipping groupBy: unknown field', bound.metaDataField)
      continue
    }
    const config = bound.config ? bound.config : op.config.defaultConfig
    buckets = op.apply(buckets, field, config, projections[bound.metaDataField])
  }

  for (const sort of pipeline.sortBys) {
    console.info('processing sort', sort)
    const op = OPERATION_REGISTRY[sort.operationKey]
    if (op?.type !== 'sort') continue

    const field = metadataFields[sort.metaDataField!]

    if (!field) {
      console.warn('Skipping sortBy: unknown field', sort.metaDataField)
      continue
    }
    const config = sort.config ? sort.config : op.config.defaultConfig
    buckets = op.apply(buckets, field, config)
  }

  return buckets
}
